import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Receipt, Calendar, DollarSign, MapPin, ChevronRight, CheckCircle,
  Clock, FileText, Download
} from 'lucide-react';
import { jobsAPI, invoicesAPI } from '../services/api';

const InvoicingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoicedJobs, setInvoicedJobs] = useState({});
  const [selectedYear, setSelectedYear] = useState('all');

  const fetchInvoicedJobs = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedYear !== 'all' ? { financial_year: selectedYear } : {};
      const response = await jobsAPI.getInvoicedJobs(params);
      setInvoicedJobs(response.data);
    } catch (error) {
      toast.error('Failed to load invoiced jobs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchInvoicedJobs();
  }, [fetchInvoicedJobs]);

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      const res = await jobsAPI.updateStatus(jobId, newStatus);
      toast.success(res.data?.archived ? 'Job completed & archived' : 'Status updated');
      fetchInvoicedJobs();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === 'unbilled') {
        const msg = [
          detail.message,
          `\nPO total: $${(detail.po_total || 0).toFixed(2)}`,
          `Invoiced: $${(detail.invoiced_to_date || 0).toFixed(2)}`,
          `Unbilled: $${(detail.unbilled || 0).toFixed(2)}`,
          `\nComplete & archive anyway?`,
        ].join('\n');
        if (window.confirm(msg)) {
          try {
            const r2 = await jobsAPI.updateStatus(jobId, newStatus, { acknowledge_unbilled: true });
            toast.success(r2.data?.archived ? 'Job completed & archived' : 'Status updated');
            fetchInvoicedJobs();
          } catch (e2) {
            toast.error(e2.response?.data?.detail?.message || 'Failed to update status');
          }
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to update status'));
      }
    }
  };

  const handleInvoiceStatusChange = async (invoiceId, newStatus) => {
    try {
      await invoicesAPI.update(invoiceId, { status: newStatus });
      toast.success('Invoice status updated');
      fetchInvoicedJobs();
    } catch (error) {
      toast.error('Failed to update invoice status');
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await invoicesAPI.downloadPdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const financialYears = Object.keys(invoicedJobs).sort().reverse();
  const allJobs = Object.entries(invoicedJobs).flatMap(([fy, jobs]) => 
    jobs.map(job => ({ ...job, financial_year: fy }))
  );

  // Calculate totals
  const totalInvoiced = allJobs.reduce((sum, job) => sum + (job.total_invoiced || 0), 0);
  const totalPaid = allJobs.reduce((sum, job) => sum + (job.total_paid || 0), 0);
  const totalOutstanding = totalInvoiced - totalPaid;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Invoicing</h1>
          <p className="text-muted-foreground">Manage invoiced jobs by financial year</p>
        </div>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-[180px] bg-background">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Financial Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {financialYears.map(fy => (
              <SelectItem key={fy} value={fy}>{fy}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Invoiced</p>
                <p className="text-2xl font-bold text-foreground">${totalInvoiced.toLocaleString()}</p>
              </div>
              <Receipt className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Paid</p>
                <p className="text-2xl font-bold text-green-400">${totalPaid.toLocaleString()}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold text-yellow-400">${totalOutstanding.toLocaleString()}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoiced Jobs by Financial Year */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : Object.keys(invoicedJobs).length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Receipt className="h-12 w-12 mb-4 opacity-50" />
            <p>No invoiced jobs found</p>
            <p className="text-sm">Jobs will appear here when their status is changed to "Invoiced"</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(invoicedJobs).sort((a, b) => b[0].localeCompare(a[0])).map(([fy, jobs]) => (
          <Card key={fy} className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                {fy}
                <span className="text-muted-foreground font-normal text-sm">
                  ({jobs.length} job{jobs.length !== 1 ? 's' : ''})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {jobs.map(job => (
                  <div key={job.id} className="p-4">
                    {/* Job Header */}
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-secondary/20 -mx-4 -mt-4 p-4 mb-4"
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground">{job.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {job.address}
                          </span>
                          <span>{job.client_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Total Invoiced</p>
                          <p className="font-bold">${job.total_invoiced?.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Paid</p>
                          <p className="font-bold text-green-400">${job.total_paid?.toLocaleString()}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>

                    {/* Invoices for this job */}
                    {job.invoices && job.invoices.length > 0 && (
                      <div className="bg-secondary/20 rounded-lg p-3 space-y-2">
                        <p className="text-sm font-medium text-muted-foreground mb-2">Invoices</p>
                        {job.invoices.map(inv => (
                          <div key={inv.id} className="flex items-center justify-between bg-background/50 p-3 rounded">
                            <div className="flex items-center gap-3">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{inv.invoice_number}</p>
                                <p className="text-xs text-muted-foreground">
                                  Due: {inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-AU') : 'N/A'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <p className="font-mono">${inv.total?.toFixed(2)}</p>
                              <Select 
                                value={inv.status} 
                                onValueChange={(v) => handleInvoiceStatusChange(inv.id, v)}
                              >
                                <SelectTrigger className={`w-[100px] h-8 text-xs ${
                                  inv.status === 'paid' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 
                                  inv.status === 'sent' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                                  'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                }`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="draft">Draft</SelectItem>
                                  <SelectItem value="sent">Sent</SelectItem>
                                  <SelectItem value="paid">Paid</SelectItem>
                                  <SelectItem value="overdue">Overdue</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => handleDownloadPdf(inv.id)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default InvoicingPage;
